import { useMutation, useQuery } from '@apollo/client';
import { useAuth } from 'oidc-react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  GET_CATEGORY_COMMENTS_FOR_OWNER_WITH_ACTIVITY_SINCE,
  GET_COMMENTS_ON_GUIDES_FOR_USER_WITH_ACTIVITY_SINCE,
  GET_USER,
  TOGGLE_EMAIL_NOTIFICATIONS,
} from './home/home.graphql';

interface UserContext {
  userId: string | undefined;
  unreadNotifications: number;
  lastReadNotifications: Date;
  updateLastReadNotifications: (date: Date) => void;
  receiveEmailNotifications: boolean;
  toggleEmailNotifications: () => void;
}

const UserContext = createContext<UserContext | undefined>(undefined);

export const useUserContext = (): UserContext => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};

export const UserContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const profile = auth.userData?.profile;
  const userId = profile?.sub;

  const [lastReadNotifications, setLastReadNotifications] = useLocalStorage('lastRead', new Date());
  const unreadNotifications = useUnreadComments(userId, lastReadNotifications);

  const { data } = useQuery(GET_USER, { variables: { userId } });
  const receiveEmailNotifications = data?.users?.[0]?.receiveEmailNotifications;

  const [toggleEmailNotifications] = useMutation(TOGGLE_EMAIL_NOTIFICATIONS, {
    variables: { input: { id: userId, receiveEmailNotifications: !receiveEmailNotifications } },
  });

  return (
    <UserContext.Provider
      value={{
        userId,
        unreadNotifications,
        lastReadNotifications,
        updateLastReadNotifications: setLastReadNotifications,
        receiveEmailNotifications: !!receiveEmailNotifications,
        toggleEmailNotifications,
      }}>
      {children}
    </UserContext.Provider>
  );
};

function useUnreadComments(userId: string | undefined, lastRead: Date): number {
  const pollInterval = 15000;

  const guideComments = useQuery(GET_COMMENTS_ON_GUIDES_FOR_USER_WITH_ACTIVITY_SINCE, {
    pollInterval,
    variables: { ownerId: userId, since: lastRead },
  });
  const categoryComments = useQuery(GET_CATEGORY_COMMENTS_FOR_OWNER_WITH_ACTIVITY_SINCE, {
    pollInterval,
    variables: { ownerId: userId, since: lastRead },
  });

  return (guideComments.data?.comments?.nodes?.length || 0) + (categoryComments.data?.comments?.nodes?.length || 0);
}

function useLocalStorage<T>(key: string, initialValue: T) {
  const [value, setValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      console.error('Failed to save to localStorage');
    }
  }, [key, value]);

  return [value, setValue];
}
